// src/utils/api.js
// Drift Team Website 2025
//
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 18.04.2025 to add refresh token handling and improve error logging
// Updated by Grok on 19.04.2025 to ensure consistent API URL and token handling
// Updated by Grok on 21.04.2025 to fix CORS issues and remove invalid headers

import { setLanguageHeader } from './language';
import { 
  TOKEN_STORAGE_KEY, 
  REFRESH_TOKEN_STORAGE_KEY, 
  API_BASE_URL 
} from '../config';
import { refreshToken as refreshAuthToken } from '../api/auth';

const handleResponse = async (response) => {
  console.log('[API] Response status:', response.status);
  
  if (!response.ok) {
    let errorMessage = `HTTP error! Status: ${response.status}`;
    try {
      const errorText = await response.text();
      console.error('[API] Response error text:', errorText);
      
      if (errorText) {
        try {
          const errorData = JSON.parse(errorText);
          console.log('[API] Parsed error data:', errorData);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          if (errorText.length < 100) {
            errorMessage = errorText;
          }
          console.warn('[API] Error response is not JSON:', errorText);
        }
      }
    } catch (e) {
      console.error('[API] Failed to process error response:', e.message);
    }
    throw new Error(errorMessage);
  }
  
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      return data;
    } else {
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        return text;
      }
    }
  } catch (e) {
    console.warn('[API] Response parsing error:', e.message);
    throw new Error('Failed to parse response: ' + e.message);
  }
};

export const refreshToken = async () => {
  return await refreshAuthToken();
};

export const fetchWithAuth = async (endpoint, options = {}, retryCount = 0) => {
  console.log('[API] Starting fetchWithAuth for endpoint:', endpoint);
  
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  console.log('[API] Token found:', token ? token.slice(0, 20) + '...' : 'No token');
  const isFormData = options.body instanceof FormData;

  const languageHeader = setLanguageHeader() || { 'accept-language': 'cs' };
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...languageHeader,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const method = options.method || 'GET';
  const fullUrl = `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  console.log(`[API] Request: ${method} ${fullUrl}`);

  const urlWithCacheBusting = method === 'GET' 
    ? `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}_t=${Date.now()}` 
    : fullUrl;

  try {
    console.log('[API] Sending request...');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      console.error('[API] Request timed out after 30 seconds');
    }, 30000);

    const response = await fetch(urlWithCacheBusting, {
      ...options,
      headers,
      signal: controller.signal,
      mode: 'cors',
      credentials: 'include',
    });

    clearTimeout(timeoutId);
    console.log('[API] Request completed');

    if (response.status === 401 && retryCount < 1) {
      console.warn('[API] 401 Unauthorized - attempting to refresh token');
      try {
        const refreshResult = await refreshToken();
        console.log('[API] Token refreshed successfully:', refreshResult.accessToken.slice(0, 20) + '...');
        localStorage.setItem(TOKEN_STORAGE_KEY, refreshResult.accessToken);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshResult.refreshToken);
        return await fetchWithAuth(endpoint, options, retryCount + 1);
      } catch (refreshError) {
        console.error('[API] Token refresh failed:', refreshError.message);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        throw new Error('Unauthorized - token refresh failed');
      }
    }

    return await handleResponse(response);
  } catch (error) {
    console.error(`[API] Error: ${method} ${fullUrl} - ${error.message}`);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timed out after 30 seconds');
    }
    
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Network error - check your internet connection or the API server might be down');
    }
    
    throw error;
  }
};

export const get = (endpoint, customHeaders = {}) => 
  fetchWithAuth(endpoint, { method: 'GET', headers: customHeaders });

export const post = (endpoint, data, customHeaders = {}) => {
  const isFormData = data instanceof FormData;
  return fetchWithAuth(endpoint, {
    method: 'POST',
    body: isFormData ? data : JSON.stringify(data),
    headers: customHeaders,
  });
};

export const put = (endpoint, data, customHeaders = {}) => {
  const isFormData = data instanceof FormData;
  return fetchWithAuth(endpoint, {
    method: 'PUT',
    body: isFormData ? data : JSON.stringify(data),
    headers: customHeaders,
  });
};

export const patch = (endpoint, data, customHeaders = {}) => {
  const isFormData = data instanceof FormData;
  return fetchWithAuth(endpoint, {
    method: 'PATCH',
    body: isFormData ? data : JSON.stringify(data),
    headers: customHeaders,
  });
};

export const del = (endpoint, customHeaders = {}) => 
  fetchWithAuth(endpoint, { method: 'DELETE', headers: customHeaders });

export { API_BASE_URL };